import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

const APP_CATEGORIES = ["TikTok", "Instagram", "YouTube", "Roblox", "Gaming", "WhatsApp", "Snapchat", "Other"];
const CONSEQUENCE_CHOICES = [
  "Mordomo informa a mãe",
  "Sem app no dia seguinte",
  "Conversa em família",
  "Apenas anotado no diário",
];

const EMPTY_FORM = {
  child_id: "",
  app_or_category: "TikTok",
  daily_limit_minutes: 60,
  window_start: "",
  window_end: "21:30",
  weekend_daily_limit_minutes: "",
  weekend_window_end: "",
  consequence_if_broken: CONSEQUENCE_CHOICES[0],
  active: true,
};

export default function Agreements() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const filterChild = params.get("child");
  const [children, setChildren] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);   // agreement being edited / null
  const [creating, setCreating] = useState(false);
  const [flash, setFlash] = useState("");

  const load = useCallback(async () => {
    try {
      const [members, agrs] = await Promise.all([
        api.get("/family/members"),
        api.get("/agreements" + (filterChild ? `?child_id=${filterChild}` : "")),
      ]);
      setChildren((members.data.members || []).filter((m) => m.role === "child"));
      setAgreements(agrs.data.items || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [filterChild]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const out = {};
    for (const a of agreements) {
      (out[a.child_id] = out[a.child_id] || []).push(a);
    }
    return out;
  }, [agreements]);

  const childrenToShow = filterChild ? children.filter((c) => c.id === filterChild) : children;

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-5xl" data-testid="agreements-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-cap">— {t("app.agreements.tag")}</p>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="agreements-title">
            {t("app.agreements.title")}
          </h1>
          <p className="font-display-italic text-[var(--ink-soft)] mt-3 max-w-2xl">
            {t("app.agreements.sub")}
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          disabled={children.length === 0}
          className="btn-outline"
          data-testid="agreements-new-btn"
        >
          + {t("app.agreements.newBtn")}
        </button>
      </div>

      {children.length === 0 && (
        <p className="mt-10 p-6 bg-[var(--surface)] font-display-italic text-[var(--ink-soft)]" data-testid="agreements-no-children">
          {t("app.agreements.noChildren")}
        </p>
      )}

      {flash && <p className="mt-6 text-sm text-[var(--oxblood)]" data-testid="agreements-flash">{flash}</p>}
      {error && <p className="mt-6 text-sm text-[var(--oxblood)]" data-testid="agreements-error">{error}</p>}

      <div className="mt-10 space-y-12" data-testid="agreements-list">
        {childrenToShow.map((child) => (
          <ChildBlock
            key={child.id}
            child={child}
            agreements={grouped[child.id] || []}
            onEdit={setEditing}
            onChange={load}
            setFlash={setFlash}
            setError={setError}
          />
        ))}
      </div>

      {(creating || editing) && (
        <AgreementModal
          kids={children}
          initial={editing || (filterChild ? { ...EMPTY_FORM, child_id: filterChild } : EMPTY_FORM)}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ChildBlock({ child, agreements, onEdit, onChange, setFlash, setError }) {
  const { t } = useLang();
  return (
    <section data-testid={`agreement-child-${child.id}`}>
      <div className="flex items-baseline justify-between gap-3 pb-3 border-b border-[var(--border)]">
        <h2 className="font-display text-2xl">{child.name}</h2>
        <p className="label-cap text-[var(--ink-faint)]">
          {child.age ? `${child.age} ${t("app.agreements.yearsOld")}` : ""} {child.whatsapp_phone ? `· ${child.whatsapp_phone}` : ""}
        </p>
      </div>
      {agreements.length === 0 ? (
        <p className="mt-4 font-display-italic text-sm text-[var(--ink-soft)]" data-testid={`agreement-empty-${child.id}`}>
          {t("app.agreements.childEmpty")}
        </p>
      ) : (
        <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)]">
          {agreements.map((a) => (
            <AgreementCard
              key={a.id}
              agreement={a}
              childHasPhone={!!child.whatsapp_phone}
              onEdit={() => onEdit(a)}
              onChange={onChange}
              setFlash={setFlash}
              setError={setError}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function statusOf(a) {
  if (a.signed_by_child) return "signed";
  if (a.talk_requested_at) return "talk";
  if (a.notify_sent_at) return "sent";
  return "draft";
}

function StatusBadge({ status }) {
  const { t } = useLang();
  const cls = {
    signed: "text-[var(--oxblood)] border-[var(--oxblood)]",
    talk: "text-[var(--ink)] border-[var(--ink)]",
    sent: "text-[var(--ink-soft)] border-[var(--ink-soft)]",
    draft: "text-[var(--ink-faint)] border-[var(--ink-faint)]",
  }[status];
  return (
    <span
      className={`label-cap inline-block px-2 py-px border ${cls}`}
      data-testid={`agreement-status-${status}`}
    >
      {t(`app.agreements.status.${status}`)}
    </span>
  );
}

function formatLimits(a, t) {
  const out = [];
  if (a.daily_limit_minutes) {
    const h = Math.floor(a.daily_limit_minutes / 60);
    const m = a.daily_limit_minutes % 60;
    const str = h && m ? `${h}h${String(m).padStart(2, "0")}` : h ? `${h}h` : `${m}min`;
    out.push(`${str} ${t("app.agreements.perDay")}`);
  }
  if (a.window_end) out.push(`${t("app.agreements.endsAt")} ${a.window_end}`);
  if (a.window_start) out.push(`${t("app.agreements.startsAt")} ${a.window_start}`);
  if (a.weekend_daily_limit_minutes) {
    out.push(`${t("app.agreements.weekend")} ${Math.round(a.weekend_daily_limit_minutes / 60)}h`);
  }
  return out.join(" · ") || t("app.agreements.noLimits");
}

function AgreementCard({ agreement, childHasPhone, onEdit, onChange, setFlash, setError }) {
  const { t } = useLang();
  const a = agreement;
  const status = statusOf(a);
  const [busy, setBusy] = useState(false);

  const notify = async () => {
    setBusy(true); setError(""); setFlash("");
    try {
      const { data } = await api.post(`/agreements/${a.id}/notify-child`);
      if (data.ok) setFlash(t("app.agreements.notifySent"));
      else setError(t("app.agreements.notifyFailed"));
      onChange();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      await api.put(`/agreements/${a.id}`, { active: !a.active });
      onChange();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("app.agreements.deleteConfirm"))) return;
    setBusy(true);
    try {
      await api.delete(`/agreements/${a.id}`);
      onChange();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <li
      className={`p-5 bg-[var(--bg)] ${!a.active ? "opacity-60" : ""}`}
      data-testid={`agreement-card-${a.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-xl">{a.app_or_category}</p>
          <p className="text-sm text-[var(--ink-soft)] mt-1" data-testid={`agreement-limits-${a.id}`}>
            {formatLimits(a, t)}
          </p>
        </div>
        <StatusBadge status={status} />
      </div>
      <p className="text-xs text-[var(--ink-faint)] mt-3">
        {t("app.agreements.consequenceLabel")}: <em>{a.consequence_if_broken}</em>
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={onEdit} disabled={busy} className="btn-ghost text-sm" data-testid={`agreement-edit-${a.id}`}>
          {t("app.agreements.edit")}
        </button>
        {!a.signed_by_child && (
          <button
            onClick={notify}
            disabled={busy || !childHasPhone}
            className="btn-ghost text-sm text-[var(--oxblood)]"
            data-testid={`agreement-notify-${a.id}`}
            title={!childHasPhone ? t("app.agreements.noPhoneHint") : ""}
          >
            {a.notify_sent_at ? t("app.agreements.notifyAgain") : t("app.agreements.notify")}
          </button>
        )}
        <button onClick={toggle} disabled={busy} className="btn-ghost text-sm" data-testid={`agreement-toggle-${a.id}`}>
          {a.active ? t("app.agreements.deactivate") : t("app.agreements.activate")}
        </button>
        <button onClick={remove} disabled={busy} className="btn-ghost text-sm text-[var(--ink-faint)]" data-testid={`agreement-delete-${a.id}`}>
          {t("app.agreements.delete")}
        </button>
      </div>
      {a.signed_by_child && (
        <p className="mt-3 text-xs text-[var(--oxblood)] font-display-italic" data-testid={`agreement-signed-info-${a.id}`}>
          {t("app.agreements.signedOn")} {(a.signed_at || "").slice(0, 10)}
        </p>
      )}
      {a.talk_requested_at && !a.signed_by_child && (
        <p className="mt-3 text-xs font-display-italic text-[var(--ink)]" data-testid={`agreement-talk-info-${a.id}`}>
          {t("app.agreements.talkRequested")}
        </p>
      )}
    </li>
  );
}

function AgreementModal({ kids, initial, onClose, onSaved }) {
  const { t } = useLang();
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    child_id: initial.child_id || (kids[0]?.id ?? ""),
    app_or_category: initial.app_or_category || "TikTok",
    daily_limit_minutes: initial.daily_limit_minutes ?? 60,
    window_start: initial.window_start || "",
    window_end: initial.window_end || "",
    weekend_daily_limit_minutes: initial.weekend_daily_limit_minutes ?? "",
    weekend_window_end: initial.weekend_window_end || "",
    consequence_if_broken: initial.consequence_if_broken || CONSEQUENCE_CHOICES[0],
    active: initial.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const change = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      const payload = {
        ...form,
        daily_limit_minutes: form.daily_limit_minutes ? Number(form.daily_limit_minutes) : null,
        weekend_daily_limit_minutes: form.weekend_daily_limit_minutes
          ? Number(form.weekend_daily_limit_minutes)
          : null,
        window_start: form.window_start || null,
        window_end: form.window_end || null,
        weekend_window_end: form.weekend_window_end || null,
      };
      if (isEdit) {
        const { child_id: _drop, ...rest } = payload;
        await api.put(`/agreements/${initial.id}`, rest);
      } else {
        await api.post("/agreements", payload);
      }
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-[var(--ink)]/40 z-50 flex items-center justify-center p-4" data-testid="agreement-modal" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg)] max-w-2xl w-full p-8 md:p-10 max-h-[90vh] overflow-y-auto"
        data-testid="agreement-form"
      >
        <p className="label-cap">— {isEdit ? t("app.agreements.editTitle") : t("app.agreements.newTitle")}</p>
        <h2 className="font-display text-3xl mt-2">{t("app.agreements.formIntro")}</h2>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label={t("app.agreements.childLabel")}>
            <select
              value={form.child_id}
              onChange={(e) => change({ child_id: e.target.value })}
              disabled={isEdit}
              required
              className="line-input"
              data-testid="agreement-child-select"
            >
              <option value="">—</option>
              {kids.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
          </Field>

          <Field label={t("app.agreements.appLabel")}>
            <select
              value={form.app_or_category}
              onChange={(e) => change({ app_or_category: e.target.value })}
              className="line-input"
              data-testid="agreement-app-select"
            >
              {APP_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <Field label={t("app.agreements.dailyLimitLabel")}>
            <input
              type="number" min={5} max={720} step={5}
              value={form.daily_limit_minutes ?? ""}
              onChange={(e) => change({ daily_limit_minutes: e.target.value })}
              className="line-input"
              data-testid="agreement-daily-limit"
            />
          </Field>

          <Field label={t("app.agreements.windowEndLabel")}>
            <input
              type="time"
              value={form.window_end}
              onChange={(e) => change({ window_end: e.target.value })}
              className="line-input"
              data-testid="agreement-window-end"
            />
          </Field>

          <Field label={t("app.agreements.windowStartLabel")}>
            <input
              type="time"
              value={form.window_start}
              onChange={(e) => change({ window_start: e.target.value })}
              className="line-input"
              data-testid="agreement-window-start"
            />
          </Field>

          <Field label={t("app.agreements.weekendLimitLabel")}>
            <input
              type="number" min={5} max={720} step={5}
              value={form.weekend_daily_limit_minutes ?? ""}
              onChange={(e) => change({ weekend_daily_limit_minutes: e.target.value })}
              className="line-input"
              data-testid="agreement-weekend-limit"
            />
          </Field>

          <div className="md:col-span-2">
            <Field label={t("app.agreements.consequenceLabel")}>
              <select
                value={form.consequence_if_broken}
                onChange={(e) => change({ consequence_if_broken: e.target.value })}
                className="line-input"
                data-testid="agreement-consequence"
              >
                {CONSEQUENCE_CHOICES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
        </div>

        {err && <p className="mt-4 text-sm text-[var(--oxblood)]" data-testid="agreement-form-error">{err}</p>}

        <div className="mt-8 flex gap-3 justify-end">
          <button type="button" onClick={onClose} disabled={saving} className="btn-ghost" data-testid="agreement-cancel">
            {t("app.agreements.cancel")}
          </button>
          <button type="submit" disabled={saving || !form.child_id} className="btn-primary" data-testid="agreement-save">
            {saving ? "…" : isEdit ? t("app.agreements.save") : t("app.agreements.create")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-cap block mb-2 text-[var(--ink-soft)]">{label}</span>
      {children}
    </label>
  );
}
